import {naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {redirectForDownload, setWindowTitle} from 'BenchlingUtil';
import {FONT_PROXIMA_10} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {Header1} from 'ui-library/Headers';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {confirm} from 'util/Modals';
import {capitalize} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

import CreateInstrumentGatewayModal from './CreateInstrumentGatewayModal';
import {
  GATEWAY_TYPE_TO_DISPLAY,
  GatewayStatus,
  GatewayType,
  InstrumentGateway,
  LATEST_BASE_GATEWAY_INSTALLER_VERSION,
} from './InstrumentGatewayConstants';
import {
  archiveGateway,
  createGateway,
  listGateways,
  createInstallerTokenFile,
  getGatewayInstaller,
  getS3GatewayInstaller,
  getCompatibilityScript,
} from './InstrumentGatewayDAO';
import {CreateS3GatewayModal, ViewBucketInfoModal} from './S3GatewayModal';

interface GatewayCellProps {
  gateway: InstrumentGateway;
}

function archiveGatewayWithMessage(gatewayId: string, refetch: () => void): void {
  const msg =
    'Archiving this gateway will archive all connections associated with it and is not reversible. To re-connect to the instrument, you will need to install a new gateway and create new connections.';
  confirm('Archive Gateway?', msg, ButtonStyle.WARNING, {
    acceptText: 'Archive Gateway',
    rejectText: 'Cancel',
    promiseCompatible: true,
    onSubmit: () => {
      withLoadingScreen(archiveGateway(gatewayId), 'Archiving gateway...').then(() => {
        refetch();
      });
    },
  });
}

interface DownloadGatewaytModalProps {
  onRequestClose: () => void;
}

function DownloadGatewayModal(props: DownloadGatewaytModalProps): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.TERTIARY_GRAY,
          text: 'Generate installer token',
          onClick: async () => {
            const {downloadUrl} = await createInstallerTokenFile();
            redirectForDownload(downloadUrl);
          },
        },
        {
          style: ButtonStyle.PRIMARY,
          cancel: true,
          text: 'Done',
        },
      ]}
      title="Download gateway"
      onRequestClose={props.onRequestClose}
      closeable={true}
    >
      <div className={css(styles.infoBanner)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={`The latest version (${LATEST_BASE_GATEWAY_INSTALLER_VERSION}) will be downloaded.`}
        />
      </div>
      The gateway installer can be found in your browser downloads folder. Please additionally download an
      installer token by clicking on the "Generate installer token" button below.
      <br />
      <br />
      During the gateway installation process, you will be prompted to upload this installer token which
      expires in 1 hour.
      <br />
      <br />
      If a new token file needs to be generated once this modal has been closed, you can generate a new token
      from the "Download" dropdown.
    </Modal>
  );
}

function DownloadTokenModal(props: DownloadGatewaytModalProps): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          cancel: true,
          text: 'Done',
        },
      ]}
      title="Generate new installer token"
      onRequestClose={props.onRequestClose}
      closeable={true}
    >
      The installer token file can be found in your browser downloads folder.
      <br />
      <br />
      During the gateway installation process, you will be prompted to upload an installer token which expires
      in 1 hour.
    </Modal>
  );
}

function DownloadScriptModal(props: DownloadGatewaytModalProps): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.TERTIARY_GRAY,
          text: 'Generate installer token',
          onClick: async () => {
            const {downloadUrl} = await createInstallerTokenFile();
            redirectForDownload(downloadUrl);
          },
        },
        {
          style: ButtonStyle.PRIMARY,
          cancel: true,
          text: 'Done',
        },
      ]}
      title="Download environment check script"
      onRequestClose={props.onRequestClose}
      closeable={true}
    >
      The PC environment compatibility check script can be found in your browser downloads folder. Please
      additionally download an installer token by clicking on the "Generate installer token" button below.
      <br />
      <br />
      The environment compatibility check script will prompt you to select this installer token, which expires
      in 1 hour.
      <br />
      <br />
      If a new token file needs to be generated once this modal has been closed, you can generate a new token
      from the "Download" dropdown.
    </Modal>
  );
}

const GATEWAY_STATUS_TO_STYLE: {[status in GatewayStatus]: keyof typeof styles} = {
  [GatewayStatus.HEALTHY]: 'healthy',
  [GatewayStatus.UNHEALTHY]: 'unhealthy',
  [GatewayStatus.UNREGISTERED]: 'unregistered',
  [GatewayStatus.GREENGRASS_NUCLEUS_STALE]: 'unhealthy',
  [GatewayStatus.GREENGRASS_COMMUNICATION_ERROR]: 'warning',
  [GatewayStatus.GREENGRASS_DEPLOYING]: 'unregistered',
  [GatewayStatus.S3_BUCKET_ERROR]: 'unhealthy',
};

const GATEWAY_STATUS_TO_TEXT: {[status in GatewayStatus]: string} = {
  [GatewayStatus.HEALTHY]: 'Open',
  [GatewayStatus.UNHEALTHY]: 'Error',
  [GatewayStatus.UNREGISTERED]: 'Pending install',
  [GatewayStatus.GREENGRASS_NUCLEUS_STALE]: 'Error',
  [GatewayStatus.GREENGRASS_COMMUNICATION_ERROR]: 'Unreachable',
  [GatewayStatus.GREENGRASS_DEPLOYING]: 'Deploying',
  [GatewayStatus.S3_BUCKET_ERROR]: 'Error',
};

const GATEWAY_STATUS_TO_TOOLTIP: {[status in GatewayStatus]: string} = {
  [GatewayStatus.HEALTHY]: '',
  [GatewayStatus.UNHEALTHY]:
    'The gateway installed on your computer is experiencing technical issues.\n\nPlease contact Benchling Support.',
  [GatewayStatus.UNREGISTERED]: 'Pending installion.',
  [GatewayStatus.GREENGRASS_NUCLEUS_STALE]:
    'Your computer is not communicating with AWS IoT service, which is necessary to run Benchling Connect. Verify that your computer is active and connected to the internet.\n\nIf the issue persists, please contact Benchling Support.',
  [GatewayStatus.GREENGRASS_COMMUNICATION_ERROR]:
    'Benchling is not receiving messages from the computer where the gateway is installed. Verify that your computer is active and connected to the internet.\n\nIf the issue persists, please contact Benchling Support.',
  [GatewayStatus.GREENGRASS_DEPLOYING]:
    'Benchling Gateway updates are in progress. Associated connection(s) may experience intermittent connectivity during this time. Deployment typically takes up to 15 minutes.',
  [GatewayStatus.S3_BUCKET_ERROR]:
    'Benchling cannot access the S3 bucket as configured. Please verify your S3 policy and the configuration.\n\nIf the issue persists, please contact Benchling Support.',
};

const GATEWAY_STATUS_TO_ICON: {[status in GatewayStatus]: BenchlingIconName} = {
  [GatewayStatus.HEALTHY]: 'status-success',
  [GatewayStatus.UNHEALTHY]: 'status-failed',
  [GatewayStatus.UNREGISTERED]: 'request-in-progress',
  [GatewayStatus.GREENGRASS_NUCLEUS_STALE]: 'status-failed',
  [GatewayStatus.GREENGRASS_COMMUNICATION_ERROR]: 'status-warning',
  [GatewayStatus.GREENGRASS_DEPLOYING]: 'hourglass-half',
  [GatewayStatus.S3_BUCKET_ERROR]: 'status-failed',
};

export function GatewayStatusIcon(props: {gateway: InstrumentGateway}): JSX.Element {
  return (
    <>
      <Tooltip
        title={GATEWAY_STATUS_TO_TOOLTIP[props.gateway.status]}
        container="body"
        delay={{show: 500, hide: 100}}
      >
        <BenchlingIcon
          className={css(styles.statusIcon, styles[GATEWAY_STATUS_TO_STYLE[props.gateway.status]])}
          icon={GATEWAY_STATUS_TO_ICON[props.gateway.status]}
        />
      </Tooltip>
    </>
  );
}

function StatusCell(props: GatewayCellProps): JSX.Element {
  return (
    <Cell>
      <GatewayStatusIcon gateway={props.gateway} />
      {capitalize(GATEWAY_STATUS_TO_TEXT[props.gateway.status])}
    </Cell>
  );
}

function ActionCell(props: GatewayCellProps & {refetch: () => void}): JSX.Element {
  return (
    <Cell>
      <span>
        <Dropdown
          closeOnInsideClick={true}
          closeOnOutsideClick={true}
          menu={<ActionDropdownMenu {...props} />}
        >
          <Button
            buttonStyle={ButtonStyle.TRANSPARENT}
            hideDropdownCaret={true}
            icon="overflow"
            size={ButtonSize.SMALL}
          />
        </Dropdown>
      </span>
    </Cell>
  );
}

function ActionDropdownMenu(props: GatewayCellProps & {refetch: () => void}): JSX.Element {
  return (
    <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
      <Section>
        <Option
          onSelect={() => {
            archiveGatewayWithMessage(props.gateway.id, props.refetch);
          }}
          isDisabled={props.gateway.isArchived}
        >
          Archive gateway
        </Option>
      </Section>
      {props.gateway.gatewayType === GatewayType.S3 && (
        <Section>
          <Option
            onSelect={async () => {
              const {roleArn} = await getS3GatewayInstaller();

              runModal(ViewBucketInfoModal, {
                roleArn,
                onRequestClose: props.refetch,
                gatewayName: props.gateway.name,
                bucketName: props.gateway.customerBucket || '',
              });
            }}
          >
            View bucket info
          </Option>
        </Section>
      )}
    </Menu>
  );
}

interface InstrumentGatewaysPageProps {
  instrumentGateways: Array<InstrumentGateway> | null;
  refetch: () => void;
}

function InstrumentGatewaysPage(props: InstrumentGatewaysPageProps): JSX.Element {
  setWindowTitle(`Benchling Gateways`);
  const {instrumentGateways} = props;
  if (instrumentGateways == null) {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <div className="settingsPage">
      <div
        className="settingsPage-contentWrapper"
        {...getTestProps({component: 'settingsPageContainer', element: 'root'})}
      >
        <div className="u-lr-split">
          <div style={{width: '70%'}}>
            <Header1>Benchling Gateways</Header1>
            <div className="help-block">
              <p>
                A Benchling gateway serves as a central hub for all your instrument connectors. Up to three
                gateways from multiple tenants can be installed on a single PC.
              </p>
              <ul>
                <li>
                  When creating a local gateway, the gateway installer only needs to be downloaded onto the
                  local machine once. However, an installer token must be generated for each tenant to install
                  gateways from multiple tenants.
                </li>
                <li>
                  When creating a S3 cloud gateway, the Benchling IAM Role must be added to your S3 bucket.
                </li>
              </ul>
            </div>
          </div>
          <div style={{float: 'right'}}>
            <div className="buttonGroup">
              <Button
                buttonStyle={ButtonStyle.PRIMARY}
                size={ButtonSize.SMALL}
                type="button"
                isDropdown={true}
                text="Setup Gateway"
              />
              <ul className="dropdown-menu pull-right">
                <li className={css(styles.dropdownSectionHeader)}>Cloud Gateway</li>
                <li>
                  <a
                    onClick={async () => {
                      const {roleArn} = await getS3GatewayInstaller();

                      runModal(CreateS3GatewayModal, {
                        roleArn,
                        onRequestClose: props.refetch,
                      });
                    }}
                  >
                    Configure S3 gateway
                  </a>
                </li>
                <li className={css(styles.dropdownSectionHeader)}>Local Gateway</li>
                <li>
                  <a
                    onClick={async () => {
                      const {downloadUrl} = await getGatewayInstaller('windows');
                      redirectForDownload(downloadUrl);
                      runModal(DownloadGatewayModal, {onRequestClose: props.refetch});
                    }}
                  >
                    Download gateway installer
                  </a>
                </li>
                {BenchlingConfig.INSTRUMENTS_ENABLE_LINUX_INSTALLER_FLOW && (
                  <li>
                    <a
                      onClick={async () => {
                        const {downloadUrl} = await getGatewayInstaller('linux');
                        redirectForDownload(downloadUrl);
                        runModal(DownloadGatewayModal, {onRequestClose: props.refetch});
                      }}
                    >
                      Download linux gateway installer
                    </a>
                  </li>
                )}

                <li>
                  <a
                    onClick={async () => {
                      const {downloadUrl} = await createInstallerTokenFile();
                      redirectForDownload(downloadUrl);
                      runModal(DownloadTokenModal, {onRequestClose: props.refetch});
                    }}
                  >
                    Generate new installer token
                  </a>
                </li>
                {window.debug && (
                  <li>
                    <a
                      onClick={() => {
                        runModal(CreateInstrumentGatewayModal, {
                          existingGateways: instrumentGateways,
                          onRequestSubmit: (name) => {
                            withLoadingScreen(
                              createGateway(name, 'GREENGRASS', undefined, true),
                              'Setting up instrument gateway...'
                            ).then((gatewayResponse) => {
                              if (gatewayResponse.tokenDownloadUrl) {
                                redirectForDownload(gatewayResponse.tokenDownloadUrl);
                              }
                              props.refetch();
                            });
                          },
                        });
                      }}
                    >
                      [DEV ONLY] Create Gateway for Local Dev
                    </a>
                  </li>
                )}
                <li>
                  <a
                    onClick={async () => {
                      const {downloadUrl} = await getCompatibilityScript();
                      redirectForDownload(downloadUrl);
                      runModal(DownloadScriptModal, {onRequestClose: props.refetch});
                    }}
                  >
                    Download PC compatibility script
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <Table numRows={instrumentGateways.length} overflowVisible={true} className={css(styles.table)}>
          <Column
            header="Name"
            renderCell={(rowIndex: number) => (
              <Cell>
                <span className={classNames({faded: instrumentGateways[rowIndex].isArchived})}>
                  {instrumentGateways[rowIndex].name}
                </span>
              </Cell>
            )}
          />
          <Column
            header="Type"
            renderCell={(rowIndex: number) => (
              <Cell>
                <span>{GATEWAY_TYPE_TO_DISPLAY[instrumentGateways[rowIndex].gatewayType]}</span>
              </Cell>
            )}
          />
          <Column
            header="Status"
            renderCell={(rowIndex: number) => <StatusCell gateway={instrumentGateways[rowIndex]} />}
          />
          <Column
            header="Actions"
            renderCell={(rowIndex: number) => (
              <ActionCell gateway={instrumentGateways[rowIndex]} refetch={props.refetch} />
            )}
          />
        </Table>
      </div>
    </div>
  );
}

export default function InstrumentGatewaysPageDataFetcher(): JSX.Element {
  const [instrumentGateways, setInstrumentGateways] = useState(null as Array<InstrumentGateway> | null);
  const refetch = (): void => {
    listGateways().then(({gateways}) => {
      setInstrumentGateways(naturalSortByKey(gateways, 'name'));
    });
  };
  useOnMount(refetch);
  if (instrumentGateways == null) {
    return <CenteredHorizontalThrobber />;
  }
  return <InstrumentGatewaysPage instrumentGateways={instrumentGateways} refetch={refetch} />;
}

export const styles = StyleSheet.create({
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  table: {
    marginTop: '35px',
  },
  infoBanner: {
    marginBottom: '10px',
  },
  statusIcon: {
    marginRight: '6px',
  },
  versionWarningIcon: {
    color: Colors.ORANGE,
    marginLeft: '6px',
  },
  healthy: {
    color: Colors.GREEN,
  },
  unhealthy: {
    color: Colors.RED_LIGHT,
  },
  warning: {
    color: Colors.ORANGE,
  },
  unregistered: {
    color: Colors.GRAY_DARK,
  },
  dropdownSectionHeader: {
    ...FONT_PROXIMA_10,
    color: Colors.GRAY_DARKER,
    fontStyle: 'italic',
    height: '23px',
    cursor: 'default',
    padding: '3px 10px',
  },
  roleArnBox: {
    display: 'flex',
    gridTemplateColumns: '4fr 1fr',
  },
  roleArnField: {
    flex: 1,
  },
  copyButton: {
    width: '97px',
    height: '28px',
    marginLeft: '10px',
    alignSelf: 'flex-end',
    fontSize: '12px',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding: '0px 16px',
  },
});
